import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import KycStatusBadge from '../components/KycStatusBadge';
import KycUploadForm from '../components/KycUploadForm';
import KycDocumentsList from '../components/KycDocumentsList';

const KycPage = () => {
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchKycData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://api.safenetbanking.com/api/kyc/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKycData(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching KYC data:', err);
      setError('Failed to load KYC information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKycData();
  }, []);

  const handleUploadSuccess = () => {
    fetchKycData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const renderKycLevelInfo = () => {
    if (!kycData) return null;
    
    const kycLevel = kycData.kyc_level || 0;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant="h6">
              KYC Verification Level: {kycLevel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Current Status: <KycStatusBadge status={kycData.kyc_status} />
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {kycData.kyc_status === 'verified' ? (
                <Alert severity="success" sx={{ width: '100%' }}>
                  Your identity has been verified. Thank you!
                </Alert>
              ) : kycData.kyc_status === 'rejected' ? (
                <Alert severity="error" sx={{ width: '100%' }}>
                  Your verification was rejected. Please submit new documents.
                </Alert>
              ) : kycData.kyc_status === 'pending' ? (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Your verification is pending review. We'll notify you once it's processed.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ width: '100%' }}>
                  Please complete KYC verification to unlock all account features.
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderKycStepper = () => {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          KYC Verification Process
        </Typography>
        <Stepper orientation="vertical">
          <Step active={true} completed={kycData?.documents?.length > 0}>
            <StepLabel>Upload Identification Documents</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Upload a government-issued ID such as a passport, driver's license, or national ID card.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Make sure your document is valid and not expired.
              </Typography>
            </StepContent>
          </Step>
          
          <Step active={kycData?.documents?.length > 0} completed={kycData?.documents?.some(d => d.document_type === 'utility_bill' || d.document_type === 'bank_statement')}>
            <StepLabel>Proof of Address</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Upload a recent utility bill or bank statement showing your name and address.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                The document should be less than 3 months old.
              </Typography>
            </StepContent>
          </Step>
          
          <Step active={kycData?.kyc_status === 'pending'} completed={kycData?.kyc_status === 'verified'}>
            <StepLabel>Verification Review</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Our team will review your submitted documents.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                This process typically takes 1-2 business days.
              </Typography>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        KYC Verification
      </Typography>
      
      <Typography variant="body1" paragraph>
        Know Your Customer (KYC) verification is required to ensure the security of your account and comply with financial regulations.
        Complete the verification process to unlock all features of your account.
      </Typography>
      
      {renderKycLevelInfo()}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          {renderKycStepper()}
        </Grid>
        
        <Grid item xs={12} md={7}>
          <KycUploadForm onUploadSuccess={handleUploadSuccess} />
          
          {kycData && kycData.documents && (
            <KycDocumentsList documents={kycData.documents} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default KycPage;
